import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  DialogActions,
  IconButton,
  Skeleton,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Search,
  FilterList,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import { CONSTANTS } from "../../../../library/common/constant";
import { Pagination } from "../../../../library/component";
import styles from "./TirthListing.module.scss";
import MultiInput from "../../../../library/common/MultiInput";
import { api } from "../../../../services";
import TirthItem from "./TirthItem";

const { ALL_TIRTH, ADD_NEW_TIRTH_BUTTON } = CONSTANTS;

const TirthListing = () => {
  const navigate = useNavigate();
  const [tirths, setTirths] = useState([]);
  const [totalTirths, setTotalTirths] = useState(0);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const [loading, setLoading] = useState(true);
  const [modalFilters, setModalFilters] = useState({});
  const [modalSelectedValues, setModalSelectedValues] = useState({});
  const [isOpen, setIsOpen] = useState({ filters: false, sort: false });
  const [states, setStates] = useState([]);
  const anchorRef = useRef(null);

  const [filters, setFilters] = useState({
    pageNo: 0,
    pageSize: 10,
    search: "",
    category: "",
    language: "",
    sortBy: "",
    order: "asc",
  });

  const handleAddNewBlog = () => {
    navigate("/tirth/add");
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== "")
      );
      // Adjust pageNo (API expects 1-indexed page numbers)
      const response = await api.get("/tirths", {
        params: { ...activeFilters, pageNo: filters?.pageNo + 1 },
      });

      setTotalTirths(response?.meta?.total ?? 0);
      setTirths(response?.data?.tirths || []);
    } catch (error) {
      console.error("Error fetching Tirths:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const updateFilters = useCallback((newFilter, resetPage = true) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilter,
      pageNo: resetPage ? 0 : newFilter.pageNo ?? prev.pageNo,
    }));
  }, []);

  const handleFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      updateFilters({ [name]: value });
    },
    [updateFilters]
  );

  // Modal filter change handler.
  const handleModalFilterChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;
    // If the field returns an array (from MultiInput), join the values.
    const filterFields = ["categories", "languages", "tags"];
    if (filterFields.includes(name)) {
      newValue = Array.isArray(newValue) ? newValue.join(",") : newValue;
    }
    setModalFilters((prev) => ({ ...prev, [name]: newValue }));
  }, []);

  // Modal and sort handlers.
  const openFilterModal = () => setIsOpen({ filters: true, sort: false });
  const openSortModal = () => setIsOpen({ filters: false, sort: true });
  const closeModal = () => setIsOpen({ filters: false, sort: false });

  // Toggle sorting for a given field.
  const handleSortToggle = (sortField) => {
    setFilters((prev) => {
      if (prev.sortBy !== sortField) {
        return { ...prev, sortBy: sortField, order: "asc", pageNo: 0 };
      } else {
        if (prev.order === "asc") {
          return { ...prev, order: "desc", pageNo: 0 };
        } else if (prev.order === "desc") {
          return { ...prev, sortBy: "", order: "asc", pageNo: 0 };
        }
      }
      return prev;
    });
  };

  // Apply modal filters to main filters.
  const applyModalFilters = () => {
    updateFilters(modalFilters);
    closeModal();
  };

  // Pagination handlers.
  const handlePageChange = (event, newPage) => {
    updateFilters({ pageNo: newPage }, false);
  };

  const handleRowsPerPageChange = (event) => {
    updateFilters({ pageSize: parseInt(event.target.value, 10) });
  };

  const fetchStates = async () => {
    try {
      const { data } = await api.get("/states");
      setStates(data?.states || []);
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  return (
    <Paper className={styles.paper_container}>
      <Box className={styles.header_search_add_container}>
        <Typography className={styles.header_title} textAlign="center">
          {ALL_TIRTH}
        </Typography>
      </Box>
      <Box
        className={styles.search_add_container}
        sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        <TextField
          className={styles.search_field}
          placeholder="Search Tirth"
          variant="outlined"
          size="small"
          name="search"
          value={filters?.search}
          onChange={handleFilterChange}
          sx={{ flex: 1, minWidth: { xs: "100%", md: 400 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          color="primary"
          variant="contained"
          size="medium"
          onClick={handleAddNewBlog}
        >
          {ADD_NEW_TIRTH_BUTTON}
        </Button>
        <Button ref={anchorRef} variant="outlined" onClick={openSortModal}>
          Sort By
        </Button>
        <Menu
          open={isOpen.sort}
          anchorEl={anchorRef.current}
          onClose={closeModal}
          disableAutoFocusItem
        >
          <MenuItem onClick={() => handleSortToggle("createdAt")}>
            <Box display="flex" alignItems="center" width="100%">
              <span>Created Date</span>
              {filters?.sortBy === "createdAt" && (
                <Box ml="auto">
                  {filters?.order === "asc" ? (
                    <ArrowUpward fontSize="small" />
                  ) : (
                    <ArrowDownward fontSize="small" />
                  )}
                </Box>
              )}
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleSortToggle("title.en")}>
            <Box display="flex" alignItems="center" width="100%">
              <span>Title</span>
              {filters?.sortBy === "title.en" && (
                <Box ml="auto">
                  {filters?.order === "asc" ? (
                    <ArrowUpward fontSize="small" />
                  ) : (
                    <ArrowDownward fontSize="small" />
                  )}
                </Box>
              )}
            </Box>
          </MenuItem>
        </Menu>
        <IconButton
          onClick={openFilterModal}
          className={styles.filterButton}
          color="primary"
        >
          <FilterList />
        </IconButton>
      </Box>

      {/* Filters Modal */}
      <Dialog open={isOpen.filters} onClose={closeModal} fullWidth>
        <DialogTitle>Filters</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <MultiInput
              options={states}
              initialValues={modalSelectedValues?.states || []}
              onChange={handleModalFilterChange}
              name="states"
              label="States"
              getLabelText={(i) => `${i?.name?.en} (${i?.code})`}
              type="object"
              setFinalValues={setModalSelectedValues}
              select
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button
            onClick={applyModalFilters}
            variant="contained"
            color="primary"
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table stickyHeader aria-label="blog table" sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell className={styles.table_header_cell}>
                Tirth Image
              </TableCell>
              <TableCell className={styles.table_header_cell}>
                Tirth Name
              </TableCell>
              <TableCell className={styles.table_header_cell}>
                District-State
              </TableCell>
              <TableCell className={styles.table_header_cell}>
                Address
              </TableCell>

              <TableCell className={styles.table_header_cell}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <TableCell key={idx}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : tirths?.length > 0 ? (
              tirths?.map((item, index) => (
                <TirthItem
                  key={`tirth-${index}`}
                  index={filters?.pageSize * filters?.pageNo + index + 1}
                  tirthItem={item}
                  getTirthList={fetchData}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className={styles.no_data_cell}>
                  <Typography variant="h6" align="center">
                    No tirths available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        totalCount={totalTirths}
        pageSize={filters?.pageSize}
        pageNo={filters?.pageNo}
        pageChangeHandler={handlePageChange}
        rowsPerPageHandler={handleRowsPerPageChange}
      />

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default TirthListing;
